import { useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import { PreventiveMaintenanceTriggerBasis } from '@evimo/types/enums/preventive-maintenance-trigger-basis';
import { PreventiveMaintenanceTriggerConditionType } from '@evimo/types/enums/preventive-maintenance-trigger-condition-type';
import { useTranslation } from 'react-i18next';
import {
  useTriggers,
  useCreateTrigger,
} from '~/app/hooks/maintenance/useTriggers';
import { useTriggersTabColumns } from '../../../hooks/useTriggersTabColumns';

export function useTriggersTab() {
  const { t } = useTranslation('maintenance');
  const { data: triggers = [], isLoading } = useTriggers();
  const createTriggerMutation = useCreateTrigger();
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const columns = useTriggersTabColumns();

  const closeModal = useCallback(() => {
    setModalOpen(false);
    form.resetFields();
  }, [form]);

  const openCreate = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleCreate = useCallback(async () => {
    const values = await form.validateFields();
    await createTriggerMutation.mutateAsync(values);
    closeModal();
  }, [closeModal, createTriggerMutation, form]);

  const basisOptions = useMemo(
    () =>
      Object.values(PreventiveMaintenanceTriggerBasis).map((v) => ({
        label: t(`triggerBasis.${v}`),
        value: v,
      })),
    [t],
  );

  const conditionTypeOptions = useMemo(
    () =>
      Object.values(PreventiveMaintenanceTriggerConditionType).map((v) => ({
        label: t(`conditionType.${v}`),
        value: v,
      })),
    [t],
  );

  return {
    triggers,
    isLoading,
    columns,
    modalOpen,
    form,
    openCreate,
    closeModal,
    handleCreate,
    isCreating: createTriggerMutation.isPending,
    basisOptions,
    conditionTypeOptions,
  };
}
